import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Autocomplete,
  Box,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { membershipService } from '../../services/membershipService';
import { apiClient } from '../../services/api';

interface CreateSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
}

const CreateSubscriptionDialog: React.FC<CreateSubscriptionDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    customerId: '',
    membershipPlanId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
  });
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => apiClient.get<{ customers: Customer[] }>('/customers?limit=100'),
    enabled: open,
  });

  // Fetch membership plans
  const { data: plansData } = useQuery({
    queryKey: ['membershipPlans'],
    queryFn: () => membershipService.getMembershipPlans({ isActive: true }),
    enabled: open,
  });

  const createSubscriptionMutation = useMutation({
    mutationFn: membershipService.createSubscription,
    onSuccess: () => {
      onSuccess();
      onClose();
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData({
      customerId: '',
      membershipPlanId: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
    });
    setSelectedCustomer(null);
  };

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customerId: customer?.id || '',
    }));
  };

  const handlePlanChange = (planId: string) => {
    const plan = ((plansData as any)?.data?.plans || (plansData as any)?.plans)?.find((p: any) => p.id === planId);
    if (plan) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(startDate);

      switch (plan.durationUnit) {
        case 'DAYS':
          endDate.setDate(endDate.getDate() + plan.duration);
          break;
        case 'MONTHS':
          endDate.setMonth(endDate.getMonth() + plan.duration);
          break;
        case 'YEARS':
          endDate.setFullYear(endDate.getFullYear() + plan.duration);
          break;
      }

      setFormData(prev => ({
        ...prev,
        membershipPlanId: planId,
        endDate: endDate.toISOString().split('T')[0],
      }));
    }
  };

  const handleStartDateChange = (startDate: string) => {
    setFormData(prev => ({ ...prev, startDate }));

    // Recalculate end date if plan is selected
    if (formData.membershipPlanId) {
      handlePlanChange(formData.membershipPlanId);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    createSubscriptionMutation.mutate(formData);
  };

  const customers = (customersData as any)?.data?.customers || (customersData as any)?.customers || [];
  const plans: any[] = (plansData as any)?.data?.plans || (plansData as any)?.plans || [];
  const selectedPlan = plans.find((p: any) => p.id === formData.membershipPlanId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Subscription</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={customers}
                getOptionLabel={(customer) => `${customer.name} (${customer.phone})`}
                value={selectedCustomer}
                onChange={(_, customer) => handleCustomerChange(customer)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required
                    placeholder="Search and select customer"
                  />
                )}
                renderOption={(props, customer) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {customer.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {customer.phone} • {customer.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Membership Plan</InputLabel>
                <Select
                  value={formData.membershipPlanId}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  label="Membership Plan"
                >
                  {plans.map((plan: any) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {plan.name} - ₹{plan.price}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.duration} {plan.durationUnit.toLowerCase()}
                          {plan.duration > 1 ? 's' : ''} • {plan.type.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            {selectedPlan && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Plan Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {selectedPlan.description}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Price:</strong> ₹{selectedPlan.price}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {selectedPlan.duration} {selectedPlan.durationUnit.toLowerCase()}
                    {selectedPlan.duration > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {selectedPlan.type.replace('_', ' ')}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              !formData.customerId ||
              !formData.membershipPlanId ||
              !formData.startDate ||
              !formData.endDate ||
              createSubscriptionMutation.isPending
            }
          >
            {createSubscriptionMutation.isPending ? 'Creating...' : 'Create Subscription'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateSubscriptionDialog;